// A single conversation's messages + composer. Shared across roles. The host
// passes `canReply` (false for observers + read-only broadcast viewers) and the
// reply mutation. docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Skeleton, Textarea } from '@fajrarabi/ui';
import { useEffect, useRef, useState } from 'react';

import { ApiError } from '~/lib/api';
import type { Message } from '~/lib/messaging';

export function MessageThread({
  messages,
  loading,
  canReply,
  sending,
  error,
  onSend,
  emptyHint,
}: {
  messages: Message[];
  loading: boolean;
  canReply: boolean;
  sending: boolean;
  error?: unknown;
  onSend: (body: string) => void;
  emptyHint?: string;
}) {
  const t = useT();
  const [body, setBody] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  function submit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setBody('');
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 space-y-3 overflow-auto p-1">
        {loading ? (
          <>
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="ms-auto h-12 w-1/2" />
          </>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">
            {emptyHint ?? t('app.messages.no_messages_yet')}
          </p>
        ) : (
          messages.map((m) => <Bubble key={m.id} message={m} />)
        )}
        <div ref={endRef} />
      </div>

      {canReply ? (
        <form
          className="mt-3 flex items-end gap-2 border-t border-burgundy-500/10 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex-1">
            {error instanceof ApiError && (
              <Alert tone="danger" className="mb-2">
                {error.message}
              </Alert>
            )}
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              placeholder={t('app.messages.compose_placeholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </div>
          <Button type="submit" disabled={sending || body.trim() === ''}>
            {t('app.messages.send')}
          </Button>
        </form>
      ) : (
        <p className="mt-3 border-t border-burgundy-500/10 pt-3 text-center text-xs text-ink-500">
          {t('app.messages.read_only')}
        </p>
      )}
    </div>
  );
}

function Bubble({ message }: { message: Message }) {
  const t = useT();
  const held = message.moderationStatus === 'flagged' || message.moderationStatus === 'blocked';
  return (
    <div className={`flex ${message.mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
          message.mine
            ? 'bg-burgundy-500 text-cream-50'
            : 'bg-white text-ink-900 ring-1 ring-burgundy-500/10'
        }`}
      >
        {!message.mine && (
          <p className="mb-0.5 text-xs font-semibold opacity-70">{message.senderName}</p>
        )}
        <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
        {held && (
          <p className="mt-1 text-[11px] italic opacity-80">{t('app.messages.held_for_review')}</p>
        )}
      </div>
    </div>
  );
}
